import { useEffect } from "react";

/**
 * useSeoMeta — met à jour dynamiquement les balises SEO côté client.
 * Googlebot exécute JavaScript et lit ces meta après hydratation (docs Google 2024).
 * Le prerender Puppeteer capture le HTML final : chaque route a son propre title,
 * sa description, son canonical et ses hreflang.
 *
 * @param {object} opts
 * @param {string} opts.title        - <title> (≤ 60 caractères recommandé)
 * @param {string} opts.description  - <meta name="description"> (≤ 155 caractères)
 * @param {string[]|string} [opts.keywords] - <meta name="keywords">
 * @param {string} [opts.canonical]  - URL absolue (défaut = URL courante)
 * @param {"index"|"noindex"} [opts.robots] - <meta name="robots">
 * @param {object} [opts.og]         - Open Graph overrides {title, description, image}
 * @param {object|array} [opts.jsonLd] - JSON-LD Schema.org à injecter
 * @param {Array<{hreflang:string,href:string}>} [opts.alternates] - hreflang réciproques
 */
export function useSeoMeta({ title, description, keywords, canonical, robots = "index, follow", og, jsonLd, alternates }) {
  const kw = Array.isArray(keywords) ? keywords.join(", ") : keywords;
  const altKey = alternates ? alternates.map((a) => `${a.hreflang}|${a.href}`).join(",") : "";

  useEffect(() => {
    if (title) document.title = title;
    _upsertMeta("name", "description", description);
    _upsertMeta("name", "robots", robots);
    if (kw) _upsertMeta("name", "keywords", kw);

    const url = canonical || window.location.origin + window.location.pathname;
    _upsertLink("canonical", url);

    // Open Graph — synchronisé avec title/description
    _upsertMeta("property", "og:title", og?.title || title);
    _upsertMeta("property", "og:description", og?.description || description);
    _upsertMeta("property", "og:url", url);
    if (og?.image) _upsertMeta("property", "og:image", og.image);

    // hreflang réciproques : on purge les alternates statiques puis on réécrit
    if (alternates && alternates.length) {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
      for (const a of alternates) {
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", a.hreflang);
        el.setAttribute("href", a.href);
        document.head.appendChild(el);
      }
    }

    // JSON-LD Schema.org — un script par bloc pour une lecture propre par les validateurs
    document.querySelectorAll('script[data-dynamic-seo="true"]').forEach((el) => el.remove());
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const block of blocks) {
        if (!block) continue;
        const ldScript = document.createElement("script");
        ldScript.type = "application/ld+json";
        ldScript.dataset.dynamicSeo = "true";
        ldScript.text = JSON.stringify(block);
        document.head.appendChild(ldScript);
      }
    }
  }, [title, description, kw, canonical, robots, og?.title, og?.description, og?.image, jsonLd, altKey, alternates]);
}

function _upsertMeta(attr, name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function _upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
